// src/pages/AddVehicle.js
import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Divider,
  Autocomplete,
  Alert,
} from "@mui/material";
import { lookupVehicleByVRM, createVehicle } from "../api/vehicles";
import useDrivers from "../hooks/useDrivers";

export default function AddVehicle() {
  const [vrmInput, setVrmInput] = useState("");
  const [loadingLookup, setLoadingLookup] = useState(false);
  const [saving, setSaving] = useState(false);

  const { drivers, loading: driversLoading } = useDrivers();

  const [form, setForm] = useState({
    vrm: "",
    vin: "",
    model_image: "",
    date_first_registered: "",
    make: "",
    make_model: "",
    seating_capacity: "",
    body_type: "",
    color: "",
    fuel_type: "",
    engine_capacity: "",
    transmission_type: "",
    driver: "",

    // PHV fields (UI only, combined into phv object on submit)
    phv_licence_number: "",
    phv_booking_date: "",       // datetime-local string
    phv_interim_date: "",       // datetime-local string
    phv_plate_expiry_date: "",  // date string
  });

  const [errors, setErrors] = useState({});
  const [genericError, setGenericError] = useState("");

  const updateField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const clearFieldError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const toUnix = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor(d.getTime() / 1000);
  };

  const handleLookup = async () => {
    if (!vrmInput.trim()) return;
    setLoadingLookup(true);
    try {
      const data = await lookupVehicleByVRM(vrmInput.trim().toUpperCase());
      setForm((prev) => ({
        ...prev,
        vrm: data.vrm || "",
        vin: data.vin || "",
        model_image: data.model_image || "",
        date_first_registered: data.date_first_registered || "",
        make: data.make || "",
        make_model: data.make_model || "",
        seating_capacity: data.seating_capacity || "",
        body_type: data.body_type || "",
        color: data.color || "",
        fuel_type: data.fuel_type || "",
        engine_capacity: data.engine_capacity || "",
        transmission_type: data.transmission_type || "",
      }));
      // Clear VRM error if lookup fills it correctly
      clearFieldError("vrm");
    } catch (err) {
      console.error(err);
      alert("Vehicle lookup failed.");
    } finally {
      setLoadingLookup(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErrors({});
    setGenericError("");

    try {
      const phv = {};
      if (form.phv_licence_number.trim()) {
        phv.licence_number = form.phv_licence_number.trim();
      }
      const bookingTs = toUnix(form.phv_booking_date);
      if (bookingTs) phv.booking_test_timestamp = bookingTs;

      const interimTs = toUnix(form.phv_interim_date);
      if (interimTs) phv.interim_test_timestamp = interimTs;

      const plateTs = toUnix(form.phv_plate_expiry_date);
      if (plateTs) phv.plate_expiry_timestamp = plateTs;

      const payload = {
        vrm: form.vrm.trim().toUpperCase(),
        vin: form.vin || "",
        model_image: form.model_image || "",
        date_first_registered: form.date_first_registered || "",
        make: form.make || "",
        make_model: form.make_model || "",
        seating_capacity: form.seating_capacity,
        body_type: form.body_type || "",
        color: form.color || "",
        fuel_type: form.fuel_type || "",
        engine_capacity: form.engine_capacity || "",
        transmission_type: form.transmission_type || "",
        driver: form.driver || null,
        phv: phv,
      };

      await createVehicle(payload);
      alert("Vehicle added successfully.");

      setForm({
        vrm: "",
        vin: "",
        model_image: "",
        date_first_registered: "",
        make: "",
        make_model: "",
        seating_capacity: "",
        body_type: "",
        color: "",
        fuel_type: "",
        engine_capacity: "",
        transmission_type: "",
        driver: "",
        phv_licence_number: "",
        phv_booking_date: "",
        phv_interim_date: "",
        phv_plate_expiry_date: "",
      });
      setVrmInput("");
    } catch (err) {
      console.error("Failed to save vehicle:", err);
      if (err.status === 400 && err.validationErrors) {
        // Express-validator style: { field: "message" }
        setErrors(err.validationErrors || {});
      } else {
        setGenericError(
          "A server error occurred while saving the vehicle. Please try again."
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const selectedDriver = drivers.find((d) => d._id === form.driver) || null;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Add Vehicle
      </Typography>

      <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
        Lookup a vehicle by VRM, review and edit details, then save to the fleet.
      </Typography>

      {genericError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setGenericError("")}
        >
          {genericError}
        </Alert>
      )}

      {/* VRM Lookup */}
      <Paper sx={{ p: 3, borderRadius: 2, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Lookup by VRM
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={8} md={6}>
            <TextField
              label="VRM"
              fullWidth
              value={vrmInput}
              onChange={(e) => {
                setVrmInput(e.target.value.toUpperCase());
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleLookup}
              disabled={!vrmInput || loadingLookup}
              sx={{ height: "100%", textTransform: "none" }}
            >
              {loadingLookup ? (
                <CircularProgress size={22} />
              ) : (
                "Lookup Vehicle"
              )}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Vehicle Details */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          mx: "auto",
        }}
      >
        {/* Identification */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Identification
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{ xs: 12, sm: 4 }}>
            <TextField
              label="VRM"
              fullWidth
              value={form.vrm}
              onChange={(e) => {
                updateField("vrm", e.target.value.toUpperCase());
                clearFieldError("vrm");
              }}
              error={!!errors.vrm}
              helperText={errors.vrm}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 4 }}>
            <TextField
              label="VIN"
              fullWidth
              value={form.vin}
              onChange={(e) => updateField("vin", e.target.value)}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Date First Registered"
              fullWidth
              value={form.date_first_registered}
              onChange={(e) =>
                updateField("date_first_registered", e.target.value)
              }
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Make"
              fullWidth
              value={form.make}
              onChange={(e) => {
                updateField("make", e.target.value);
                clearFieldError("make");
              }}
              error={!!errors.make}
              helperText={errors.make}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Make & Model"
              fullWidth
              value={form.make_model}
              onChange={(e) => {
                updateField("make_model", e.target.value);
                clearFieldError("make_model");
              }}
              error={!!errors.make_model}
              helperText={errors.make_model}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Appearance */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Appearance
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Colour"
              fullWidth
              value={form.color}
              onChange={(e) => {
                updateField("color", e.target.value);
                clearFieldError("color");
              }}
              error={!!errors.color}
              helperText={errors.color}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Body Type"
              fullWidth
              value={form.body_type}
              onChange={(e) => updateField("body_type", e.target.value)}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Seating Capacity"
              fullWidth
              value={form.seating_capacity}
              onChange={(e) => updateField("seating_capacity", e.target.value)}
            />
          </Grid>

          {/* Model image preview */}
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Model Image URL"
              fullWidth
              value={form.model_image}
              onChange={(e) => updateField("model_image", e.target.value)}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            {form.model_image ? (
              <Box
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                  height: 140,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f7f7f7",
                }}
              >
                <Box
                  component="img"
                  src={form.model_image}
                  alt="Vehicle preview"
                  sx={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 1 }}
              >
                No preview available.
              </Typography>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Mechanical */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Mechanical
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Fuel Type"
              fullWidth
              value={form.fuel_type}
              onChange={(e) => updateField("fuel_type", e.target.value)}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Engine Capacity"
              fullWidth
              value={form.engine_capacity}
              onChange={(e) => updateField("engine_capacity", e.target.value)}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Transmission Type"
              fullWidth
              value={form.transmission_type}
              onChange={(e) =>
                updateField("transmission_type", e.target.value)
              }
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* PHV / Licensing */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          PHV / Licensing
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12, sm: 6}}>
            <TextField
              label="Licence Number"
              fullWidth
              value={form.phv_licence_number}
              onChange={(e) =>
                updateField("phv_licence_number", e.target.value)
              }
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Plate Expiry Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.phv_plate_expiry_date}
              onChange={(e) =>
                updateField("phv_plate_expiry_date", e.target.value)
              }
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Booking Test Date & Time"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.phv_booking_date}
              onChange={(e) =>
                updateField("phv_booking_date", e.target.value)
              }
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Interim Test Date & Time"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.phv_interim_date}
              onChange={(e) =>
                updateField("phv_interim_date", e.target.value)
              }
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Assignment */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Driver Assignment
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              fullWidth
              options={drivers}
              loading={driversLoading}
              value={selectedDriver}
              onChange={(e, newValue) =>
                updateField("driver", newValue ? newValue._id : "")
              }
              getOptionLabel={(option) =>
                `${option.firstname || ""} ${option.lastname || ""}`.trim()
              }
              isOptionEqualToValue={(o, v) => o._id === v._id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign Driver (optional)"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {driversLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="outlined"
            sx={{ textTransform: "none" }}
            onClick={() => {
              setForm({
                vrm: "",
                vin: "",
                model_image: "",
                date_first_registered: "",
                make: "",
                make_model: "",
                seating_capacity: "",
                body_type: "",
                color: "",
                fuel_type: "",
                engine_capacity: "",
                transmission_type: "",
                driver: "",
                phv_licence_number: "",
                phv_booking_date: "",
                phv_interim_date: "",
                phv_plate_expiry_date: "",
              });
              setVrmInput("");
              setErrors({});
              setGenericError("");
            }}
          >
            Reset
          </Button>

          <Button
            variant="contained"
            sx={{ textTransform: "none" }}
            onClick={handleSave}
            disabled={saving || !form.vrm}
          >
            {saving ? <CircularProgress size={22} /> : "Save Vehicle"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
